<template>
  <div class="flex flex-row items-center px-3 py-4">
    <a-tooltip placement="top" v-if="hasData && activityDisplayText !== '--'">
      <template #title>
        <div
          v-if="latestActivity && latestActivity.description"
          class="flex flex-col"
        >
          <span>{{ latestActivity.description }}</span>
          <span>Last activity on {{ formattedDateTime }}</span>
        </div>
        <span v-else>Last activity on {{ formattedDateTime }}</span>
      </template>
      <span class="text-sm text_color cursor-pointer">
        {{ activityDisplayText }}
      </span>
    </a-tooltip>
    <span v-else class="text-sm text_color">--</span>
  </div>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import { formatCompactRelativeTime } from "@/src/utils/dateTimeUtils.js";
import { formatDateTime } from "@/src/utils/utils.js";

const props = defineProps({
  updatedAt: { type: String, default: "" },
  activity: { type: Object, default: null },
  timezone: { type: String, default: "" },
});

const latestActivity = ref(props.activity);

// Check if we have any data to display (either activity or updatedAt)
const hasData = computed(() => {
  return latestActivity.value || props.updatedAt;
});

const relativeTime = computed(() => {
  const dateToFormat =
    latestActivity.value && latestActivity.value.created_time
      ? latestActivity.value.created_time
      : props.updatedAt;

  if (!dateToFormat) return "--";
  return formatCompactRelativeTime(dateToFormat, props.timezone);
});

const activityDisplayText = computed(() => {
  const timeAgo = relativeTime.value;

  if (!hasData.value || timeAgo === "--") return "--";
  if (latestActivity.value && latestActivity.value.description) {
    return `${latestActivity.value.description} • ${timeAgo}`;
  }

  return timeAgo;
});

const formattedDateTime = computed(() => {
  const dateToFormat =
    latestActivity.value && latestActivity.value.created_time
      ? latestActivity.value.created_time
      : props.updatedAt;

  if (!dateToFormat) return "";
  return props.timezone
    ? formatDateTime(dateToFormat, "full", props.timezone)
    : formatDateTime(dateToFormat, "full");
});

watch(
  () => props.activity,
  (newActivity) => {
    latestActivity.value = newActivity;
    // Debug logging
    if (newActivity && !newActivity.description) {
      console.warn("Activity missing description:", newActivity);
    }
  },
);
</script>
